import type { ApiErrorBody, ApiErrorCode, WiderrufConsentDetails } from '@/types/api'

/**
 * Codes for failures that never reached the backend, so they cannot collide
 * with the envelope's own codes (§7.5). They exist so that "no status" stops
 * being the only thing distinguishing a dropped connection from a bug.
 */
export const TRANSPORT = {
  network: 'NETWORK_ERROR',
  timeout: 'TIMEOUT',
  aborted: 'ABORTED',
  unexpected: 'UNEXPECTED_ERROR',
} as const

type TransportCode = (typeof TRANSPORT)[keyof typeof TRANSPORT]

/** Raised by the client itself when a response contradicts the request. */
type ClientErrorCode = 'CUSTOMER_MISMATCH' | 'SIGN_IN_AFTER_ACCEPT_FAILED'

/** Every code an `ApiError` can carry, so a branch on a misspelt code fails to compile. */
export type ErrorCode = ApiErrorCode | TransportCode | ClientErrorCode

export class ApiError extends Error {
  readonly status: number | null
  readonly code: ErrorCode | null
  readonly body: ApiErrorBody | null
  readonly fieldErrors: Record<string, string>

  constructor(init: {
    message: string
    status?: number | null
    code?: ErrorCode | null
    body?: ApiErrorBody | null
    fieldErrors?: Record<string, string>
    cause?: unknown
  }) {
    super(init.message, { cause: init.cause })
    this.name = 'ApiError'
    this.status = init.status ?? null
    this.code = init.code ?? null
    this.body = init.body ?? null
    this.fieldErrors = init.fieldErrors ?? {}
  }

  /**
   * The request never reached the server. This is deliberately narrower than
   * "has no status": an error we merely wrapped — a render crash, a failed
   * invariant — has no status either, and reporting that as an outage sends
   * people to check their wifi over a bug in the app.
   */
  get isNetworkError(): boolean {
    return this.code === TRANSPORT.network
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }

  get isForbidden(): boolean {
    return this.status === 403
  }

  get isNotFound(): boolean {
    return this.status === 404
  }

  get isRetryable(): boolean {
    // An abort was someone's decision, and an unexpected error repeats itself;
    // neither gets a second attempt.
    if (this.code === TRANSPORT.timeout) return true
    return this.isNetworkError || (this.status !== null && this.status >= 500)
  }

  /**
   * What a check-in refused for a missing Widerruf consent asks the customer to agree to; `null`
   * for any other error, or when the details are not what the contract promises, so a screen can
   * never show a consent text the server did not send.
   */
  get widerrufConsent(): WiderrufConsentDetails | null {
    if (this.code !== 'WIDERRUF_CONSENT_REQUIRED') return null

    const { text, textVersion, windowEndsOn } = this.body?.details ?? {}
    if (
      typeof text !== 'string' ||
      typeof textVersion !== 'string' ||
      typeof windowEndsOn !== 'string'
    ) {
      return null
    }
    return { text, textVersion, windowEndsOn }
  }

  static fromResponse(status: number, body: ApiErrorBody | null): ApiError {
    return new ApiError({
      message: body?.message || fallbackMessage(status),
      status,
      code: (body?.code as ApiErrorCode | undefined) ?? null,
      body,
      fieldErrors: body?.details?.fields ?? {},
    })
  }

  static from(error: unknown): ApiError {
    if (error instanceof ApiError) return error

    if (error instanceof DOMException && error.name === 'TimeoutError') {
      return new ApiError({
        message: 'Die Anfrage hat zu lange gedauert. Bitte versuchen Sie es erneut.',
        code: TRANSPORT.timeout,
        cause: error,
      })
    }
    if (error instanceof DOMException && error.name === 'AbortError') {
      return new ApiError({
        message: 'Die Anfrage wurde abgebrochen.',
        code: TRANSPORT.aborted,
        cause: error,
      })
    }

    // `fetch` reports every failure to reach the server as a TypeError.
    if (error instanceof TypeError) {
      return new ApiError({
        message: 'Keine Verbindung zum Server. Bitte prüfen Sie Ihre Internetverbindung.',
        code: TRANSPORT.network,
        cause: error,
      })
    }

    return new ApiError({
      message: error instanceof Error ? error.message : 'Es ist ein Fehler aufgetreten.',
      code: TRANSPORT.unexpected,
      cause: error,
    })
  }
}

function fallbackMessage(status: number): string {
  if (status === 400) return 'Die Eingaben sind unvollständig oder ungültig.'
  if (status === 401) return 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.'
  if (status === 403) return 'Für diesen Bereich fehlt Ihnen die Berechtigung.'
  if (status === 404) return 'Nicht gefunden.'
  if (status === 422) return 'Die Eingabe ist unvollständig oder ungültig.'
  if (status === 429) return 'Zu viele Versuche. Bitte warten Sie einen Moment.'
  if (status >= 500)
    return 'Der Server antwortet gerade nicht. Bitte versuchen Sie es später erneut.'
  return 'Es ist ein Fehler aufgetreten.'
}
